import { ChargeRange } from '@app/domain/ChargeRange'
import { PartySizeDuration } from '@app/domain/PartySizeDuration'
import { ReasonDateRangeFilter } from '@app/domain/ReasonDateRangeFilter'
import { ReasonScheduleRule } from '@app/domain/ReasonScheduleRule'
import { Charge } from './Charge'

export class BookingReason {

    constructor(
        public id: string,
        public displayName: string,
        public displayOrder: number,
        public description: string | null,
        public contributesToKitchenVelocity: boolean,
        public schedule: ReasonScheduleRule[],
        public dateRangeFilters: ReasonDateRangeFilter[],
        public areaBookingOrder: string[] | null,
        public bookingDurations: PartySizeDuration[],
        public diningInformation: string | null,
        public minimumPartySize: number | null,
        public maximumPartySize: number | null,
        public linkExclusive: boolean,
        public dailyCoverLimit: number | null,
        public bookingLimit: number | null,
        public coverLimit: number | null,
        public cancellationChargeRanges: ChargeRange[],
        public depositChargeRanges: ChargeRange[],
        public largePartyNoAvailabilityMessage: string | null,
        public merchantBookingsRequireChargeByDefault: boolean | null,
        public preorderMenuId: string | null,
        public sendPreOrderMenuId: string | null,
        public sendPreOrderMenuMinPartySize: number | null,
        public preBookingWindowMinutes: number | null,
        public displayedOpeningTimes: string | null,
        public reserveWithGoogleEnabled: boolean = false,
        public imageId: string | null = null,
        public calendarColour: string | null = null,
        public isActive: boolean = true
    ) { }

    removeImage() {
        this.imageId = null
    }

    addScheduleRule(rule: ReasonScheduleRule) {
        this.schedule.push(rule)
    }

    updateScheduleRule(rule: ReasonScheduleRule) {
        const index = this.schedule.findIndex(r => r.id === rule.id)
        this.schedule[index] = rule
    }

    removeScheduleRule(rule: ReasonScheduleRule) {
        const index = this.schedule.findIndex(r => r.id === rule.id)
        this.schedule.splice(index, 1)
    }

    addDateRangeFilter(filter: ReasonDateRangeFilter) {
        this.dateRangeFilters.push(filter)
    }

    updateDateRangeFilter(filter: ReasonDateRangeFilter) {
        const index = this.dateRangeFilters.findIndex(f => f.id === filter.id)
        this.dateRangeFilters[index] = filter
    }

    removeDateRangeFilter(filter: ReasonDateRangeFilter) {
        const index = this.dateRangeFilters.findIndex(f => f.id === filter.id)
        this.dateRangeFilters.splice(index, 1)
    }

    appliesToDate(date: Date, timeZone: string): boolean {
        const dateInSchedule = this.schedule
            .some(rule => rule.appliesToDate(date))
        if (!dateInSchedule) {
            return false
        }
        return this.isDateInDateRange(date, timeZone)
    }

    isDateInDateRange(date: Date, timeZone: string): boolean {
        const restrictedToDateRanges = this.dateRangeFilters.length > 0
        if (!restrictedToDateRanges) {
            return true
        }
        return this.dateRangeFilters.some(filter => filter.appliesToDate(date, timeZone))
    }

    durationForPartySize(date: Date, partySize: number, timeZone: string): number | null {
        if (!this.appliesToDate(date, timeZone)) {
            return null
        }
        const reasonScheduleRuleDuration = this.schedule
            .map(rule => rule.durationForPartySize(date, partySize))
            .filter(duration => duration !== null)[0] || null
        if (reasonScheduleRuleDuration !== null) {
            return reasonScheduleRuleDuration
        }
        const largestDurationThatFits = this.bookingDurations
            .filter(duration => duration.partySize !== null)
            .filter(duration => duration.partySize! <= partySize)
            .sort((a, b) => b.partySize! - a.partySize!)[0] || null
        return largestDurationThatFits?.duration ?? null
    }

    cancellationChargeForPartySize(partySize: number): Charge | null {
        const applicableRange = this.cancellationChargeRanges.find(range => {
            const meetsMinSize = range.minPartySize === null || partySize >= range.minPartySize
            const meetsMaxSize = range.maxPartySize === null || partySize <= range.maxPartySize
            return meetsMinSize && meetsMaxSize
        })
        if (!applicableRange || applicableRange.unitAmount === null) {
            return null
        }
        return new Charge(
            partySize,
            applicableRange.minPartySize,
            applicableRange.maxPartySize,
            applicableRange.unitAmount,
            applicableRange.isPerCover
        )
    }

    depositChargeForPartySize(partySize: number): Charge | null {
        const applicableRange = this.depositChargeRanges.find(range => {
            const meetsMinSize = range.minPartySize === null || partySize >= range.minPartySize
            const meetsMaxSize = range.maxPartySize === null || partySize <= range.maxPartySize
            return meetsMinSize && meetsMaxSize
        })
        if (!applicableRange || applicableRange.unitAmount === null) {
            return null
        }
        return new Charge(
            partySize,
            applicableRange.minPartySize,
            applicableRange.maxPartySize,
            applicableRange.unitAmount,
            applicableRange.isPerCover
        )
    }
}
